import { CheckCircle2, Mic, Type, Video } from "lucide-react";
import { mediaSrc } from "@/lib/media";
import { fmtDur } from "@/lib/format";

export const FeedbackSection = ({ content, fbDone, setFbDone, onSubmit, fbKind, setFbKind, fbFile, setFbFile, fbForm, setFbForm, fbBusy, feedbacks }) => (
  <section id="feedback" data-testid="feedback-section" className="pb-24">
    <div className="bg-white/70 backdrop-blur border border-white/60 rounded-3xl shadow-[0_8px_40px_rgba(26,46,38,0.08)] p-8 md:p-10">
      <h2 className="font-serif font-light text-3xl md:text-4xl tracking-tight mb-6">Share your experience.</h2>

      {content?.feedback_enabled && (
        fbDone ? (
          <div data-testid="feedback-thanks" className="mb-10 rounded-2xl border border-accent/30 bg-accent/5 p-6 flex items-start gap-4">
            <CheckCircle2 size={26} className="text-accent shrink-0 mt-0.5" />
            <div>
              <p className="font-serif text-xl">Thank you for sharing.</p>
              <p className="text-sm text-muted-foreground mt-1">{fbDone}</p>
              <button data-testid="feedback-again" onClick={() => setFbDone(null)} className="mt-4 rounded-full border border-accent text-accent px-5 py-2 text-sm hover:bg-accent hover:text-white transition-colors">
                Share another
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} data-testid="feedback-form" className="mb-10 space-y-4">
            <div className="flex flex-wrap gap-2" data-testid="feedback-kind-tabs">
              {[["text", "Text", Type], ["audio", "Audio", Mic], ["video", "Video", Video]].map(([k, label, Icon]) => (
                <button
                  type="button" key={k} data-testid={`feedback-kind-${k}`}
                  onClick={() => { setFbKind(k); setFbFile(null); }}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm transition-colors ${fbKind === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-white"}`}
                >
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>
            <div className="space-y-4">
              <input
                data-testid="feedback-name" value={fbForm.name}
                onChange={(e) => setFbForm({ ...fbForm, name: e.target.value })}
                placeholder="Your name (optional)"
                className="w-full bg-transparent border-b border-border py-2.5 outline-none focus:border-accent transition-colors placeholder:text-muted-foreground/50"
              />
              {fbKind === "text" ? (
                <div>
                  <p className="text-xs text-muted-foreground mb-2 flex items-start justify-between gap-3">
                    <span>("Please share your personal experience of the program. Kindly avoid listing the topics or content covered in the class.") · Maximum 120 words</span>
                    <span data-testid="feedback-word-count" className="shrink-0 tabular-nums">{(fbForm.message || "").trim() ? (fbForm.message || "").trim().split(/\s+/).length : 0}/120</span>
                  </p>
                  <textarea
                    data-testid="feedback-message" rows={4} value={fbForm.message}
                    onChange={(e) => {
                      const words = e.target.value.trim().split(/\s+/).filter(Boolean);
                      const capped = words.length > 120 ? words.slice(0, 120).join(" ") : e.target.value;
                      setFbForm({ ...fbForm, message: capped });
                    }}
                    placeholder="Share your experience…"
                    className="w-full bg-white/70 border border-border rounded-xl px-4 py-3 outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all resize-y placeholder:text-muted-foreground/50"
                  />
                </div>
              ) : (
                <input
                  type="file" data-testid="feedback-file"
                  accept={fbKind === "audio" ? "audio/*" : "video/*"}
                  onChange={(e) => setFbFile(e.target.files?.[0] || null)}
                  className="w-full text-sm text-muted-foreground file:mr-3 file:rounded-full file:border-0 file:bg-primary file:text-primary-foreground file:px-4 file:py-2 file:text-sm"
                />
              )}
            </div>
            {fbKind !== "text" && (
              <p className="text-xs text-muted-foreground">
                Max length: {fbKind === "audio" ? fmtDur(content?.audio_max_sec || 120) : fmtDur(content?.video_max_sec || 60)}. Your {fbKind} will appear after admin approval.
              </p>
            )}
            <button type="submit" disabled={fbBusy} data-testid="feedback-submit" className="justify-self-start rounded-full bg-primary text-primary-foreground px-7 py-3 text-sm hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60">
              {fbBusy ? "Sending…" : "Send"}
            </button>
          </form>
        )
      )}

      {feedbacks.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-5 max-h-[70vh] overflow-y-auto pr-2" data-testid="feedback-list">
          {feedbacks.map((f) => (
            <div key={f.id} className="rounded-2xl border border-border bg-background/60 p-5">
              {f.kind === "audio" && f.media_url ? (
                <audio controls src={mediaSrc(f.media_url)} className="w-full" data-testid={`feedback-audio-${f.id}`} />
              ) : f.kind === "video" && f.media_url ? (
                <video controls src={mediaSrc(f.media_url)} className="w-full rounded-xl max-h-72" data-testid={`feedback-video-${f.id}`} />
              ) : (
                <p className="text-foreground/90 leading-relaxed">"{f.message}"</p>
              )}
              <p className="mt-3 text-sm font-medium text-accent">— {f.name}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">Be the first to share your experience.</p>
      )}
    </div>
  </section>
);
